/**
 * Universities Tab Component
 * Displays university entities with CRUD operations
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { GraduationCap, Plus, Edit, Trash2, MapPin, Mail } from "lucide-react";

export function UniversitiesTab({ 
  universities, 
  onAdd, 
  onEdit, 
  onDelete 
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-slate-400">Manage university entities for student assignment</p>
        <Button
          onClick={() => onAdd()}
          className="bg-cyan-400 text-black hover:bg-cyan-300"
          data-testid="add-university-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add University
        </Button>
      </div>
      
      {universities.length === 0 ? (
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No universities yet</p>
            <p className="text-slate-500 text-sm mt-1">Add your first university to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {universities.map((uni) => (
            <Card 
              key={uni.id} 
              className="bg-[#151A23] border-slate-700 hover:border-cyan-500/50 transition-colors" 
              data-testid={`university-card-${uni.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{uni.name}</CardTitle>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(uni)}
                      className="text-slate-400 hover:text-cyan-400"
                      data-testid={`edit-university-${uni.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(uni.id)}
                      className="text-slate-400 hover:text-red-400"
                      data-testid={`delete-university-${uni.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {uni.address && (
                  <div className="flex items-start gap-2 text-sm text-slate-400 mb-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{uni.address}, {uni.city}, {uni.state} {uni.zip_code}</span>
                  </div>
                )}
                {uni.contact_email && (
                  <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{uni.contact_email}</span>
                  </div>
                )}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-700">
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-cyan-400">{uni.user_count || 0}</p>
                    <p className="text-xs text-slate-500">Users</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-white">{uni.student_count || 0}</p>
                    <p className="text-xs text-slate-500">Students</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
